import { useEffect, useRef, useMemo, memo } from 'react';
import { useQuery } from 'react-query';
import { useSearchParams } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Popup, useMap, LayersControl } from 'react-leaflet';
import { Phone, Navigation } from 'lucide-react';
import { customerAPI } from '../services/api';
import L from 'leaflet';

// Fix for default marker icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

// Map controller to change view without remounting
const MapController = memo(({ center, zoom }) => {
  const map = useMap();
  useEffect(() => {
    if (center && zoom) {
      map.setView(center, zoom);
    }
  }, [map, center, zoom]);
  return null;
});

// Custom marker component that auto-opens popup when selected - memoized
const CustomerMarker = memo(({ customer, isSelected }) => {
  const markerRef = useRef(null);

  useEffect(() => {
    if (isSelected && markerRef.current) {
      markerRef.current.openPopup();
    }
  }, [isSelected]);

  return (
    <Marker
      ref={markerRef}
      position={[customer.latitude, customer.longitude]}
    >
      <Popup>
        <div className="p-2 min-w-[200px]">
          <h3 className="font-bold text-base mb-2">{customer.customerName}</h3>
          <p className="text-sm text-gray-600">{customer.area}</p>
          <p className="text-sm mb-3">
            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
              customer.status === 'Done' ? 'bg-green-100 text-green-800' :
              customer.status === 'Pending Installation' ? 'bg-blue-100 text-blue-800' :
              customer.status === 'In Process' ? 'bg-yellow-100 text-yellow-800' :
              customer.status === 'Canceled' ? 'bg-red-100 text-red-800' :
              'bg-gray-100 text-gray-800'
            }`}>
              {customer.status}
            </span>
          </p>
          {customer.phone && (
            <div className="flex items-center gap-2 text-sm mb-2">
              <Phone size={14} className="text-gray-500" />
              <a href={`tel:${customer.phone}`} className="text-blue-600 hover:underline">
                {customer.phone}
              </a>
            </div>
          )}
          <a
            href={`https://www.google.com/maps/dir/?api=1&destination=${customer.latitude},${customer.longitude}`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 text-sm text-black bg-green-400 hover:bg-green-500 px-3 py-1.5 rounded-md mt-2"
          >
            <Navigation size={14} />
            Get Directions
          </a>
        </div>
      </Popup>
    </Marker>
  );
});

const MapView = () => {
  const [searchParams] = useSearchParams();
  const { data: validLocations = [], isLoading } = useQuery(
    ['customers-map'],
    () => customerAPI.getForMap().then(res => res.data.data),
    { staleTime: 5 * 60 * 1000 }
  );

  // Check if lat/lng provided in URL params
  const paramLat = searchParams.get('lat');
  const paramLng = searchParams.get('lng');
  const paramCustomerId = searchParams.get('customerId');

  // Memoize center and zoom to prevent unnecessary updates
  const { center, zoom } = useMemo(() => {
    const newCenter = (paramLat && paramLng)
      ? [parseFloat(paramLat), parseFloat(paramLng)]
      : validLocations.length > 0
      ? [validLocations[0].latitude, validLocations[0].longitude]
      : [34.7772, 32.4249]; // Paphos, Cyprus

    const newZoom = (paramLat && paramLng) ? 15 : 11;
    return { center: newCenter, zoom: newZoom };
  }, [paramLat, paramLng, validLocations]);

  // Default center for initial map render
  const defaultCenter = [34.7772, 32.4249];

  return (
    <div className="space-y-3 md:space-y-4">
      <h1 className="text-2xl md:text-3xl font-bold">Map View</h1>
      <div
        className="card p-0 overflow-hidden"
        style={{ height: 'calc(100vh - 180px)', minHeight: '400px', maxHeight: '700px' }}
      >
        {isLoading ? (
          <div className="flex items-center justify-center h-full">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
          </div>
        ) : (
          <MapContainer
            center={defaultCenter}
            zoom={11}
            className="h-full w-full"
            attributionControl={false}
          >
            <MapController center={center} zoom={zoom} />
            <LayersControl position="topright">
              <LayersControl.BaseLayer checked name="Road Map">
                <TileLayer
                  url="http://{s}.google.com/vt/lyrs=m&x={x}&y={y}&z={z}"
                  maxZoom={20}
                  subdomains={['mt0', 'mt1', 'mt2', 'mt3']}
                />
              </LayersControl.BaseLayer>
              <LayersControl.BaseLayer name="Satellite">
                <TileLayer
                  url="http://{s}.google.com/vt/lyrs=s&x={x}&y={y}&z={z}"
                  maxZoom={20}
                  subdomains={['mt0', 'mt1', 'mt2', 'mt3']}
                />
              </LayersControl.BaseLayer>
              <LayersControl.BaseLayer name="Hybrid">
                <TileLayer
                  url="http://{s}.google.com/vt/lyrs=s,h&x={x}&y={y}&z={z}"
                  maxZoom={20}
                  subdomains={['mt0', 'mt1', 'mt2', 'mt3']}
                />
              </LayersControl.BaseLayer>
            </LayersControl>
            {validLocations.map((customer) => (
              <CustomerMarker
                key={customer.id}
                customer={customer}
                isSelected={paramCustomerId && String(customer.id) === String(paramCustomerId)}
              />
            ))}
          </MapContainer>
        )}
      </div>
      <p className="text-xs md:text-sm text-gray-600">
        Showing {validLocations.length} customers with GPS coordinates
      </p>
    </div>
  );
};

export default MapView;
